import React from 'react'
import { AlertTriangle, Clock, CheckCircle, RefreshCw, Calendar } from 'lucide-react'
import { WeekLockStatus } from '../../services/timesheetService'
import { formatDateLabel } from '../../utils/dateLabel'

interface WeekLockStatusBannerProps {
  lockStatus: WeekLockStatus
  onRefresh: () => void
  isRefreshing?: boolean
}

const WeekLockStatusBanner: React.FC<WeekLockStatusBannerProps> = ({ 
  lockStatus, 
  onRefresh,
  isRefreshing = false
}) => {
  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case 'DRAFT':
        // DRAFT 為系統自動產生，計時器頁面不需要提示
        return null

      case 'REJECTED':
        return {
          type: 'warning',
          icon: <AlertTriangle className="w-5 h-5" />,
          gradientFrom: 'from-amber-50',
          gradientTo: 'to-orange-50/50',
          borderColor: 'border-amber-200/60',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          textColor: 'text-amber-800',
          subTextColor: 'text-amber-600',
          title: '申報已駁回',
          message: '您可以修改時間記錄後重新提交'
        }
      
      case 'SUBMITTED':
        return {
          type: 'pending',
          icon: <Clock className="w-5 h-5" />,
          gradientFrom: 'from-orange-50',
          gradientTo: 'to-yellow-50/50',
          borderColor: 'border-orange-200/60',
          iconBg: 'bg-orange-100',
          iconColor: 'text-orange-600',
          textColor: 'text-orange-800',
          subTextColor: 'text-orange-600',
          title: '審核中',
          message: '申報已提交，如需修改請在申報頁面撤回申報'
        }
      
      case 'APPROVED':
        return {
          type: 'success',
          icon: <CheckCircle className="w-5 h-5" />,
          gradientFrom: 'from-green-50',
          gradientTo: 'to-emerald-50/50',
          borderColor: 'border-green-200/60',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          textColor: 'text-green-800',
          subTextColor: 'text-green-600',
          title: '已核准',
          message: '申報已核准，無法再修改時間記錄'
        }
      
      default:
        return null
    }
  }

  const config = getStatusConfig(lockStatus.status)
  if (!config) return null

  // ⛔ 這裡印的是週次邊界（日期標籤），toLocaleDateString 會讓「本週鎖到哪天」
  //    顯示成隔天——而夥伴正是靠這行判斷還能不能補登。見 ../../utils/dateLabel。
  const formatDate = (dateString: string) => formatDateLabel(dateString)

  return (
    <div className={`
      relative overflow-hidden rounded-2xl border ${config.borderColor}
      bg-gradient-to-r ${config.gradientFrom} ${config.gradientTo}
      p-5 animate-fade-in
    `}>
      {/* 背景裝飾 */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-30">
        <div className={`w-full h-full ${config.iconBg} rounded-full blur-3xl`} />
      </div>
      
      <div className="relative flex items-start gap-4">
        {/* 圖示 */}
        <div className={`
          flex-shrink-0 w-12 h-12 rounded-xl ${config.iconBg}
          flex items-center justify-center shadow-sm
        `}>
          <span className={config.iconColor}>{config.icon}</span>
        </div>
        
        {/* 內容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className={`font-semibold ${config.textColor} mb-1`}>
                {config.title}
              </h3>
              <p className={`${config.subTextColor} text-sm mb-3`}>
                {config.message}
              </p>
            </div>
            
            {/* 重新檢查按鈕 */}
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`
                flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                text-xs font-medium ${config.textColor}
                bg-white/60 hover:bg-white/80
                border border-white/50
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isRefreshing ? '檢查中...' : '重新檢查'}</span>
            </button>
          </div>
          
          {/* 週次範圍 */}
          <div className={`
            inline-flex items-center gap-2 px-3 py-1.5 rounded-lg
            bg-white/50 border border-white/50
          `}>
            <Calendar className={`w-4 h-4 ${config.iconColor}`} />
            <span className={`text-sm ${config.textColor}`}>
              <span className="font-medium">週次：</span>
              {formatDate(lockStatus.weekStart)} - {formatDate(lockStatus.weekEnd)}
            </span>
          </div>
          
          {/* 駁回原因 */}
          {lockStatus.status === 'REJECTED' && lockStatus.rejectionReason && (
            <div className="mt-3 p-3 rounded-xl bg-white/50 border border-white/50">
              <p className={`${config.textColor} font-medium text-sm mb-1`}>
                駁回原因：
              </p>
              <p className={`${config.subTextColor} text-sm leading-relaxed`}>
                {lockStatus.rejectionReason}
              </p>
            </div>
          )}
          
          {/* 修改期限 */}
          {lockStatus.status === 'REJECTED' && lockStatus.revisionDeadline && (
            <div className="mt-2 flex items-center gap-2">
              <Clock className={`w-4 h-4 ${config.iconColor}`} />
              <span className={`text-sm ${config.textColor}`}>
                <span className="font-medium">修改期限：</span>
                {formatDate(lockStatus.revisionDeadline)}
              </span>
            </div>
          )}
          
          {/* 鎖定提示 */}
          {lockStatus.isLocked && (
            <p className={`mt-3 text-xs ${config.subTextColor}`}>
              如需修改，請聯繫管理員
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default WeekLockStatusBanner
